import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, CalendarClock, Check, Play, Plus, Trash2 } from 'lucide-react';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Campo, Texto as CampoTexto } from '../componentes/Campos';
import { useAuth } from '../contextos/AuthContext';
import {
  agendamentoVazio,
  listarTarefas,
  removerAgendamento,
  rodarAgendaAgora,
  salvarAgendamento,
  useAgendamentos,
  type RascunhoAgendamento,
  type TarefaDoAgente,
} from '../servicos/agenda';

/**
 * A agenda do agente.
 *
 * Aqui você escreve o que quer que aconteça, em que dias e a que hora. O
 * sistema acorda de 15 em 15 minutos e faz o que está marcado.
 *
 * Uma decisão que sustenta a honestidade desta tela: as tarefas que o agente
 * AINDA NÃO SABE fazer aparecem na lista e podem ser agendadas — planejar antes
 * de poder é legítimo —, mas vêm marcadas, e quando chega a hora o histórico
 * registra "ainda não executa" em vez de fingir sucesso.
 *
 * Sem isso a agenda ficaria bonita e muda: tarefa agendada que não roda é
 * invisível por natureza, porque ninguém repara na ausência de um efeito.
 */
const DIAS = [
  { n: 1, curto: 'Seg' },
  { n: 2, curto: 'Ter' },
  { n: 3, curto: 'Qua' },
  { n: 4, curto: 'Qui' },
  { n: 5, curto: 'Sex' },
  { n: 6, curto: 'Sáb' },
  { n: 0, curto: 'Dom' },
] as const;

export function AbaAgenda() {
  const { agendamentos, carregando } = useAgendamentos();
  const { perfil } = useAuth();
  const [tarefas, setTarefas] = useState<TarefaDoAgente[]>([]);
  const [editando, setEditando] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [rodando, setRodando] = useState(false);
  const [relatorio, setRelatorio] = useState<Array<{ nome: string; situacao: string }> | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    listarTarefas().then(setTarefas).catch(() => setTarefas([]));
  }, []);

  if (carregando) return <Carregando />;

  const porTarefa = (id: string) => tarefas.find((t) => t.id === id);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-est bg-est/10 px-4 py-3 text-sm text-tinta-2">
        <p>
          <strong className="text-tinta">Escreva o que o agente deve fazer, e quando.</strong> O
          sistema acorda de 15 em 15 minutos e executa o que estiver marcado para aquele horário,
          no fuso de Brasília.
        </p>
        <p className="mt-1.5">
          Algumas tarefas ainda não existem — elas aparecem marcadas, dá para agendar e o histórico
          registra que não rodaram. <strong className="text-tinta">Nada acontece em silêncio.</strong>
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          disabled={rodando || agendamentos.length === 0}
          onClick={async () => {
            setErro(null);
            setRelatorio(null);
            setRodando(true);
            try {
              setRelatorio(await rodarAgendaAgora());
            } catch (e) {
              setErro((e as { message?: string }).message ?? 'Não deu para rodar.');
            } finally {
              setRodando(false);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm hover:border-marca hover:text-marca disabled:opacity-50"
          title="Passa pela agenda agora, sem esperar o horário."
        >
          <Play className="size-4" aria-hidden />
          {rodando ? 'Rodando…' : 'Rodar agora'}
        </button>
        <button
          type="button"
          onClick={() => {
            setNovo(true);
            setEditando(null);
          }}
          className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
        >
          <Plus className="size-4" aria-hidden />
          Novo agendamento
        </button>
      </div>

      {erro && <Erro titulo="Não deu certo" detalhe={erro} />}

      {relatorio && (
        <div className="rounded-md border border-regua-2 bg-superficie px-4 py-3">
          <p className="mb-1.5 text-xs font-medium">O que aconteceu agora</p>
          <ul className="flex flex-col gap-1 text-xs text-tinta-2">
            {relatorio.length === 0 ? (
              <li>Nenhum agendamento cadastrado.</li>
            ) : (
              relatorio.map((r, i) => (
                <li key={i}>
                  <strong className="text-tinta">{r.nome}</strong>: {traduzir(r.situacao)}
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {novo && (
        <Formulario
          inicial={agendamentoVazio()}
          tarefas={tarefas}
          usuarioId={perfil?.id ?? ''}
          aoConcluir={() => setNovo(false)}
        />
      )}

      {agendamentos.length === 0 && !novo ? (
        <Vazio
          icone={<CalendarClock className="size-8" />}
          titulo="Nenhum agendamento"
          descricao="Escolha uma tarefa, os dias da semana e o horário. O que você escrever nas instruções vai junto quando a tarefa rodar."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {agendamentos.map((a) => {
            const t = porTarefa(a.tarefa);
            const aberto = editando === a.id;
            return (
              <li key={a.id} className="rounded-md border border-regua-2 bg-superficie">
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <span className={`font-medium ${a.ativo ? '' : 'text-tinta-3 line-through'}`}>
                    {a.nome}
                  </span>
                  <span className="flex gap-0.5">
                    {DIAS.map((d) => (
                      <span
                        key={d.n}
                        className={`rounded px-1 py-0.5 font-mono text-[0.6rem] ${
                          a.diasDaSemana.includes(d.n)
                            ? 'bg-marca text-marca-tinta'
                            : 'bg-regua text-tinta-3'
                        }`}
                      >
                        {d.curto}
                      </span>
                    ))}
                  </span>
                  <span className="num text-sm">{a.horario}</span>

                  {t && !t.executa && (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-c-res"
                      title={t.quando}
                    >
                      <AlertTriangle className="size-3.5" aria-hidden />
                      ainda não executa
                    </span>
                  )}
                  {!a.ativo && (
                    <span className="rounded bg-tinta-3/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-tinta-3">
                      PAUSADO
                    </span>
                  )}

                  <div className="ml-auto flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setEditando(aberto ? null : a.id)}
                      className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                    >
                      {aberto ? 'Fechar' : 'Editar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removerAgendamento(a.id)}
                      aria-label={`Remover ${a.nome}`}
                      className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </button>
                  </div>
                </div>

                {(a.ultimoResultado || t) && (
                  <div className="border-t border-regua px-4 py-2 text-xs text-tinta-2">
                    {t && <p className="text-tinta-3">{t.descricao}</p>}
                    {a.ultimoResultado && (
                      <p className={a.ultimoResultadoOk ? 'text-c-via' : 'text-c-res'}>
                        Última vez: {a.ultimoResultado}
                      </p>
                    )}
                  </div>
                )}

                {aberto && (
                  <Formulario
                    id={a.id}
                    inicial={a}
                    tarefas={tarefas}
                    usuarioId={perfil?.id ?? ''}
                    aoConcluir={() => setEditando(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Traduz o motivo técnico para algo que se lê sem manual. */
function traduzir(situacao: string): string {
  const mapa: Record<string, string> = {
    DESLIGADO: 'pausado, não rodou',
    DIA_ERRADO: 'hoje não é dia dele',
    HORA_NAO_CHEGOU: 'ainda não chegou a hora de hoje',
    JA_RODOU_HOJE: 'já rodou hoje',
    HORARIO_INVALIDO: 'o horário cadastrado não é válido',
  };
  return mapa[situacao] ?? situacao;
}

function Formulario({
  id,
  inicial,
  tarefas,
  usuarioId,
  aoConcluir,
}: {
  id?: string;
  inicial: RascunhoAgendamento;
  tarefas: TarefaDoAgente[];
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [d, setD] = useState<RascunhoAgendamento>(() => ({ ...inicial }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof RascunhoAgendamento>(c: K, v: RascunhoAgendamento[K]) =>
    setD((x) => ({ ...x, [c]: v }));

  const alternarDia = (n: number) =>
    editar(
      'diasDaSemana',
      d.diasDaSemana.includes(n) ? d.diasDaSemana.filter((x) => x !== n) : [...d.diasDaSemana, n],
    );

  const tarefaEscolhida = tarefas.find((t) => t.id === d.tarefa);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!d.nome.trim()) return setErro('Dê um nome ao agendamento.');
    if (!d.tarefa) return setErro('Escolha o que o agente deve fazer.');
    if (d.diasDaSemana.length === 0) return setErro('Escolha ao menos um dia da semana.');
    if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(d.horario)) {
      return setErro('O horário precisa estar no formato HH:MM, como 08:30.');
    }
    setErro(null);
    setSalvando(true);
    try {
      await salvarAgendamento(id ?? null, d, usuarioId);
      aoConcluir();
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-3 border-t border-regua px-4 py-4">
      {erro && <Erro titulo={erro} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <CampoTexto
          rotulo="Nome"
          valor={d.nome}
          aoMudar={(v) => editar('nome', v ?? '')}
          dica="Como você reconhece este agendamento."
        />
        <Campo rotulo="O que o agente faz">
          <select
            value={d.tarefa}
            onChange={(e) => editar('tarefa', e.target.value)}
            className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          >
            <option value="">Escolha…</option>
            {tarefas.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
                {t.executa ? '' : ' — ainda não executa'}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Horário" dica="Horário de Brasília, no formato HH:MM.">
          <input
            type="time"
            value={d.horario}
            onChange={(e) => editar('horario', e.target.value)}
            className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          />
        </Campo>
      </div>

      {tarefaEscolhida && !tarefaEscolhida.executa && (
        <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
          <strong className="text-tinta">Esta tarefa ainda não existe.</strong>{' '}
          {tarefaEscolhida.quando}. Dá para deixar agendada — quando chegar a hora, o histórico vai
          registrar que ela não rodou, em vez de dar a entender que rodou.
        </p>
      )}

      <Campo rotulo="Dias da semana">
        <div className="flex flex-wrap gap-1.5 py-1">
          {DIAS.map((dia) => (
            <button
              key={dia.n}
              type="button"
              onClick={() => alternarDia(dia.n)}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                d.diasDaSemana.includes(dia.n)
                  ? 'bg-marca text-marca-tinta'
                  : 'border border-regua-2 text-tinta-2 hover:border-marca'
              }`}
            >
              {dia.curto}
            </button>
          ))}
        </div>
      </Campo>

      <Campo
        rotulo="Instruções para o agente"
        dica="O que você quer que ele leve em conta. Vai junto quando a tarefa rodar."
      >
        <textarea
          rows={3}
          value={d.instrucoes ?? ''}
          onChange={(e) => editar('instrucoes', e.target.value || null)}
          placeholder="Ex.: priorizar os fornecedores da região Sul; avisar se algum preço subir mais de 10%."
          className="w-full rounded border border-regua-2 bg-papel px-3 py-2 text-sm outline-none focus:border-marca"
        />
      </Campo>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={d.ativo}
          onChange={(e) => editar('ativo', e.target.checked)}
        />
        <span className="text-tinta-2">Ativo — desmarque para pausar sem apagar</span>
      </label>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={salvando}
          className="inline-flex items-center gap-1.5 rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          <Check className="size-4" aria-hidden />
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
